import { readCurrentProjectDiskSnapshot, analyzeCurrentProjectStructure } from '@/features/assistant/tools/project-tools'
import { summarizeTimeline } from '@/features/assistant/tools/timeline-tools'
import { getAssistantPlanStatus } from '@/features/assistant/tools/planning-tools'

export interface McpResourceDescriptor {
  uri: string
  name: string
  description?: string
  mimeType?: string
}

export interface McpResourceReadResult {
  contents: Array<{
    uri: string
    mimeType: string
    text: string
  }>
}

const MCP_RESOURCE_DESCRIPTORS: McpResourceDescriptor[] = [
  {
    uri: 'project://current/snapshot',
    name: 'Current Project Snapshot',
    description: 'Persisted project.json / media-links / render queue / export file snapshot for the current project.',
    mimeType: 'application/json',
  },
  {
    uri: 'project://current/structure',
    name: 'Current Project Structure',
    description: 'Structured analysis of the current project and timeline.',
    mimeType: 'application/json',
  },
  {
    uri: 'timeline://current/summary',
    name: 'Current Timeline Summary',
    description: 'Read-only summary of current tracks, items and total duration.',
    mimeType: 'application/json',
  },
  {
    uri: 'assistant://current/plan-status',
    name: 'Current Assistant Plan Status',
    description: 'Current draft plan / running job status from local assistant store.',
    mimeType: 'application/json',
  },
]

export function getMcpResourceCatalog(): McpResourceDescriptor[] {
  return [...MCP_RESOURCE_DESCRIPTORS]
}

export async function readMcpResource(uri: string): Promise<McpResourceReadResult> {
  switch (uri) {
    case 'project://current/snapshot': {
      const snapshot = await readCurrentProjectDiskSnapshot()
      return {
        contents: [{ uri, mimeType: 'application/json', text: JSON.stringify(snapshot, null, 2) }],
      }
    }
    case 'project://current/structure': {
      const structure = analyzeCurrentProjectStructure()
      return {
        contents: [{ uri, mimeType: 'application/json', text: JSON.stringify(structure, null, 2) }],
      }
    }
    case 'timeline://current/summary': {
      const summary = summarizeTimeline()
      return {
        contents: [{ uri, mimeType: 'application/json', text: JSON.stringify(summary, null, 2) }],
      }
    }
    case 'assistant://current/plan-status': {
      const status = getAssistantPlanStatus()
      return {
        contents: [{ uri, mimeType: 'application/json', text: JSON.stringify(status, null, 2) }],
      }
    }
    default:
      throw new Error(`Unknown MCP resource URI: ${uri}`)
  }
}
